import React, { useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';

const Help = () => {
    const [bugReport, setBugReport] = useState({
        title: '',
        description: '',
        severity: 'medium'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleBugReportChange = (e) => {
        const { name, value } = e.target;
        setBugReport(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleBugReportSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // TODO: Replace with actual API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Mock successful submission
            setSubmitStatus({
                type: 'success',
                message: 'Bug report submitted successfully! We will review it shortly.'
            });

            // Reset form
            setBugReport({
                title: '',
                description: '',
                severity: 'medium'
            });
        } catch (error) {
            setSubmitStatus({
                type: 'error',
                message: 'Failed to submit bug report. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Help & Support</h1>
                <p className="text-gray-600">Get help and report issues</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Documentation */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Documentation</h2>

                        <div className="space-y-4">
                            <div>
                                <h3 className="font-medium text-gray-900 mb-2">How to Create an Order</h3>
                                <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                                    <li>Navigate to the "Order Request" page</li>
                                    <li>Select quantities for E-PIN, Scratch, or E-TopUp items</li>
                                    <li>Review the calculated totals</li>
                                    <li>Click "Submit Order" to place your request</li>
                                </ol>
                            </div>

                            <div>
                                <h3 className="font-medium text-gray-900 mb-2">Order Status Tracking</h3>
                                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                    <li><span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Pending</span> - Order submitted, awaiting processing</li>
                                    <li><span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Processing</span> - Order is being processed</li>
                                    <li><span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Completed</span> - Order has been fulfilled</li>
                                    <li><span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Cancelled</span> - Order was cancelled</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-medium text-gray-900 mb-2">Account Management</h3>
                                <p className="text-sm text-gray-600">
                                    View your account information, credit limits, and order history on the "Account Info" page.
                                    Contact support if you need to update your account details.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
                        <div className="space-y-2 text-sm text-gray-600">
                            <p><strong>Email:</strong> support@company.com</p>
                            <p><strong>Phone:</strong> +1 (555) 123-4567</p>
                            <p><strong>Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM EST</p>
                            <p><strong>Emergency:</strong> +1 (555) 999-8888 (24/7)</p>
                        </div>
                    </div>
                </div>

                {/* Bug Report Form */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Report a Bug</h2>

                    {submitStatus && (
                        <div className={`mb-4 p-3 rounded-md ${submitStatus.type === 'success'
                                ? 'bg-green-50 border border-green-200 text-green-600'
                                : 'bg-red-50 border border-red-200 text-red-600'
                            }`}>
                            {submitStatus.message}
                        </div>
                    )}

                    <form onSubmit={handleBugReportSubmit} className="space-y-4">
                        <Input
                            label="Bug Title"
                            name="title"
                            value={bugReport.title}
                            onChange={handleBugReportChange}
                            required
                            placeholder="Brief description of the issue"
                        />

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Severity Level
                            </label>
                            <select
                                name="severity"
                                value={bugReport.severity}
                                onChange={handleBugReportChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="low">Low - Minor issue</option>
                                <option value="medium">Medium - Moderate issue</option>
                                <option value="high">High - Major issue</option>
                                <option value="critical">Critical - System breaking</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={bugReport.description}
                                onChange={handleBugReportChange}
                                required
                                rows={4}
                                placeholder="Please describe the issue in detail, including steps to reproduce..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isSubmitting}
                            className="w-full"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Help; 